import React, { useEffect, useState, useMemo } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Autocomplete,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Collapse,
  Card,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { SchoolData, useSchools } from "../hooks/useSchools";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useMedium } from "../components/layout/Layout";
import { useSchoolIdForAdmin } from "../hooks/useSchoolIdForAdmin";
import { useSessionClass } from "../contexts/SessionClassContext";
import { useError } from "../contexts/ErrorContext";
import { useSessions } from "../hooks/useSession";
import { useStudents } from "../hooks/useStudents";
import { useQueryClient, useQueries } from "@tanstack/react-query";
import axios from "axios";
import { useTeacher } from "../hooks/useTeacher";
import { useSubjects } from "../hooks/useSubjects";
import { useHolidays } from "../hooks/useHolidays";
import { useClasses } from "../hooks/useClasses";
import { getTeacherWeekSchedule } from "../services/timetableService";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import CodeIcon from "@mui/icons-material/Code";
import PublicIcon from "@mui/icons-material/Public";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import LanguageIcon from "@mui/icons-material/Language";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ClassIcon from "@mui/icons-material/Class";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BadgeIcon from "@mui/icons-material/Badge";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import SubjectIcon from "@mui/icons-material/Subject";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { keyframes } from "@emotion/react";
import Tooltip from "@mui/material/Tooltip";

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.92) translateY(10px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const heroScale = keyframes`
  from { transform: scale(1.04); }
  to   { transform: scale(1); }
`;

const AnimatedNumber: React.FC<{ value: number | string }> = ({ value }) => {
  const isNum = typeof value === "number";
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!isNum) return;
    const target = value as number;
    if (target === 0) { setDisplay(0); return; }
    let startTime: number | null = null;
    const duration = 950;
    const animate = (ts: number) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setDisplay(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
      else setDisplay(target);
    };
    const raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [value, isNum]);
  return <>{isNum ? display : value}</>;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [data, setData] = useState<SchoolData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const { user, isLoading: authLoading } = useAuth();
  const isAppUser = user?.roles?.includes("APP_USER");
  const isStudent = user?.roles?.includes("STUDENT");
  const { useSchoolById } = useSchools(false, { enableQuery: false });
  const { selectedSession, selectedClass } = useSessionClass();
  const { selectedMedium } = useMedium();
  const { data: adminSchoolId, isLoading: schoolIdLoading } = useSchoolIdForAdmin(
    isAppUser || user?.roles?.includes("ADMIN") || user?.roles?.includes("SCHOOL_ADMIN") || user?.roles?.includes("TEACHER") || isStudent
      ? undefined
      : user?.id
  );

  const schoolId = user?.schoolId;
  const { data: school, isLoading: schoolLoading } = useSchoolById(schoolId ?? undefined);
  const { studentsQuery } = useStudents(false, schoolId ?? undefined, !isStudent && !isAppUser);

  const { teachersQuery } = useTeacher(schoolId ?? undefined, authLoading);
  const { subjectsQuery } = useSubjects(authLoading, schoolId ?? undefined);
  const { classesQuery } = useClasses(schoolId ?? undefined);
  const { activeSessionQuery } = useSessions(schoolId ?? undefined);
  const { useholidaysQueryBySession } = useHolidays(selectedSession?.id);
  const classApplicationQueries = useQueries({
    queries: (classesQuery.data ?? []).map((cls: any) => ({
      queryKey: ["applicationFormsBySessionAndClass", selectedSession?.id, cls.id],
      queryFn: () =>
        axios
          .get(`${process.env.REACT_APP_API_URL}/application-forms/sessionId/classId`, {
            params: { sessionId: selectedSession?.id, classId: cls.id },
          })
          .then((r) => r.data as any[]),
      enabled: !!selectedSession?.id && !authLoading,
      staleTime: 60000,
    })),
  });

  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const { user: authUser } = useAuth();
  const { updateSchoolMutation } = useSchools(false, { enableQuery: false });
  const { showSuccess, showError } = useError();
  const [schoolImageFile, setSchoolImageFile] = useState<File | null>(null);
  const [mediums, setMediums] = useState<string[]>([]);
  const [newMedium, setNewMedium] = useState<string>("");
  const [mediumDialogOpen, setMediumDialogOpen] = useState(false);
  const [imageVersion, setImageVersion] = useState(0);

  const isSchoolAdmin = authUser?.roles?.includes("SCHOOL_ADMIN");
  const isTeacher = authUser?.roles?.includes("TEACHER");
  const canManageImages = isSchoolAdmin || isTeacher;

  useEffect(() => {
    if (school?.medium) {
      setMediums(Array.isArray(school.medium) ? school.medium : [school.medium]);
    } else {
      setMediums([]);
    }
  }, [school?.medium]);

  const displaySession = activeSessionQuery.data ?? selectedSession;

  useEffect(() => {
    if (schoolId && !isStudent && !isAppUser) {
      studentsQuery.refetch();
    }
  }, [schoolId, isStudent, isAppUser]);

  const { useStaffByEmail } = useTeacher(schoolId ?? undefined);
  const staffQuery = useStaffByEmail(isTeacher ? user?.email : undefined);
  const teacherId = staffQuery.data?.id;

  const dayOrder = useMemo(() => ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'], []);



  const totalStudentsCount = useMemo(() => {
    if (isAppUser) return "-";
    if (!studentsQuery.data) return "-";
    return studentsQuery.data.length;
  }, [studentsQuery.data, isAppUser]);

  const pendingApplicationsCount = useMemo(() => {
    return classApplicationQueries
      .flatMap((q) => (q.data as any[]) ?? [])
      .filter((a: any) => a.status !== false && a.isRegistered?.toLowerCase() === "pending")
      .length;
  }, [classApplicationQueries]);

  const schoolImageUrl = useMemo(() => {
    if (school && school.schoolImage) {
      return `${apiBaseUrl}/schools/school-image/${school.schoolImage}?v=${imageVersion}`;
    }
    return `/3010ca78-6194-45ee-a965-c6550ad0e444.jpg`;
  }, [apiBaseUrl, school?.schoolImage, imageVersion]);

  const upcomingHolidays = useMemo(() => {
    const holidays = useholidaysQueryBySession.data || [];
    if (!holidays.length) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const allDates: { date: Date; description: string; typeName: string; typeDescription: string }[] = [];
    holidays.forEach((holiday: any) => {
      (holiday.dates || []).forEach((dateStr: string) => {
        // Parse "DD-MM-YYYY" format correctly
        const parts = dateStr.split("-");
        if (parts.length !== 3) return;
        const date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        if (!isNaN(date.getTime()) && date >= today) {
          allDates.push({
            date,
            description: holiday.description,
            typeName: holiday.typeName,
            typeDescription: holiday.typeDescription,
          });
        }
      });
    });
    return allDates.sort((a, b) => a.date.getTime() - b.date.getTime()).slice(0, 5);
  }, [useholidaysQueryBySession.data]);

  const handleMenuClick = (idx: number) => {
    setOpenMenu(openMenu === idx ? null : idx);
  };

  const handleSchoolImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file?.type.startsWith("image/")) setSchoolImageFile(file);
    else setSchoolImageFile(null);
  };

  const handleUpdateImages = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (school && schoolImageFile) {
      const updatedSchoolData = {
        ...school,
        schoolImage: schoolImageFile.name,
      };
      updateSchoolMutation.mutate(
        { id: school.id!, schoolData: updatedSchoolData, schoolImageFile },
        {
          onSuccess: () => {
            setSchoolImageFile(null);
            showSuccess(t("school_images_updated_successfully"));
            if (schoolId) {
              queryClient.invalidateQueries({ queryKey: ["schoolById", schoolId] });
              queryClient.invalidateQueries({ queryKey: ["schoolImageInfo", schoolId] });
              setImageVersion((prev) => prev + 1);
            }
          },
          onError: (error: any) => {
            const message = error?.response?.data?.message || t("failed_to_update_school_images");
            showError(message);
          },
        }
      );
    }
  };

  const handleAddMedium = () => {
    if (newMedium.trim() && !mediums.includes(newMedium.trim().toUpperCase())) {
      setMediums([...mediums, newMedium.trim().toUpperCase()]);
      setNewMedium("");
    }
  };

  const handleRemoveMedium = (m: string) => setMediums(mediums.filter((x) => x !== m));

  const handleUpdateMediums = () => {
    if (school) {
      updateSchoolMutation.mutate(
        { id: school.id!, schoolData: { ...school, medium: mediums } },
        {
          onSuccess: () => {
            showSuccess(t("school_images_updated_successfully"));
            setTimeout(() => window.location.reload(), 1500);
          },
          onError: (error: any) => {
            const message = error?.response?.data?.message || t("failed_to_update_school_images");
            showError(message);
          },
        }
      );
    }
  };

  const hasImageChanges = schoolImageFile;
  const hasMediumChanges =
    JSON.stringify(mediums.sort()) !== JSON.stringify((school?.medium || []).sort());

  const importantLinks = useMemo(() => {
    if (isStudent) {
      return [
        {
          label: t("exam_links") || "Exam Links",
          color: "linear-gradient(135deg,#0168EA,#3790ff)",
          icon: "🎓",
          links: [
            { text: t("link_mp_board_results"), url: "https://result.mponline.gov.in/Result/ResultSecondExam?rspbmhsh=MgfgfJHGFFtytytykoReiLme7klyzA1kUfdsfdsfDDSFDSFDsdai4eyrZZGnPzi9K1pc7sKI0vs==" },
            { text: t("link_retotaling_answerbook"), url: "https://mpbse.mponline.gov.in/ras/ras/rasstatus" },
            { text: t("link_jee"), url: "https://jeeadv.ac.in/imp_dates.html" },
            { text: t("link_neet"), url: "https://neet.careerguide.com/registration/#13" },
          ],
        },
        {
          label: t("scholarship_links") || "Scholarship Links",
          color: "linear-gradient(135deg,#2E7D32,#66BB6A)",
          icon: "🎓",
          links: [
            { text: t("link_mptaasc"), url: "https://www.tribal.mp.gov.in/MPTAAS" },
            { text: t("link_nsp"), url: "https://scholarships.gov.in/otrapplication/#/" },
          ],
        },
        {
          label: t("certificate_links") || "Certificate Links",
          color: "linear-gradient(135deg,#E65100,#FF8C00)",
          icon: "📄",
          links: [
            { text: t("link_cast_certificate"), url: "https://www.mpedistrict.gov.in/MPL/ShowServiceDetail.aspx?param=tRzkQMyq7hexCSoHBRxcVqsPT+tO5xhGU7mvoavrIpBbUJq4uNmlNQH218UlhJ/vaHjgiM5gJ3k=" },
            { text: t("link_income_certificate"), url: "https://www.mpedistrict.gov.in/MPL/ServiceRegistrationStatus.aspx?by=RegNo" },
          ],
        },
        {
          label: t("govt_bodies_links") || "Government Bodies",
          color: "linear-gradient(135deg,#7B1FA2,#AB47BC)",
          icon: "🏛️",
          links: [
            { text: t("link_npci"), url: "https://www.npci.org.in/" },
            { text: t("link_mp_employee_selection_board"), url: "https://esb.mp.gov.in/Hindi/h_default.html" },
            { text: t("link_staff_selection_commission"), url: "https://ssc.gov.in/home" },
            { text: t("link_union_public_service_commission"), url: "https://upsc.gov.in/" },
          ],
        },
        {
          label: t("other_resources_links") || "Other Resources",
          color: "linear-gradient(135deg,#00838F,#26C6DA)",
          icon: "🌐",
          links: [
            { text: t("link_diksha_portal"), url: "https://pmevidya.education.gov.in/diksha.html" },
            { text: t("link_download_aadhaar"), url: "https://myaadhaar.uidai.gov.in/genricDownloadAadhaar/en" },
          ],
        },
      ];
    }
    return [
      {
        label: t("education_portal"),
        color: "linear-gradient(135deg,#0168EA,#3790ff)",
        icon: "🎓",
        links: [
          { text: "educationportal.mp.gov.in", url: "https://educationportal.mp.gov.in/" },
          { text: "educationportal.mp.gov.in/sLogin.aspx", url: "https://educationportal.mp.gov.in/sLogin.aspx" },
          { text: "sederp.educationportal3.in", url: "https://sederp.educationportal3.in/" },
          { text: "educationportal.mp.gov.in/ESB", url: "https://www.educationportal.mp.gov.in/ESB/Public/ESB_Main.aspx" },
        ],
      },
      {
        label: t("board_of_secondary_education"),
        color: "linear-gradient(135deg,#C62828,#EF5350)",
        icon: "📚",
        links: [
          { text: "mpbse.mponline.gov.in/MPBSE", url: "https://mpbse.mponline.gov.in/MPBSE/MPBSE" },
          { text: "mpbse.mponline.gov.in/Login", url: "https://mpbse.mponline.gov.in/Login" },
        ],
      },
      {
        label: t("vimarsh_portal"),
        color: "linear-gradient(135deg,#00838F,#26C6DA)",
        icon: "🌐",
        links: [
          { text: "vimarsh.mp.gov.in", url: "https://www.vimarsh.mp.gov.in/(S(2mvfyindj3gm1bezhmftjnxl))/school/index.aspx" },
        ],
      },
    ];
  }, [t, isStudent]);

  useEffect(() => {
    if (school) {
      setData(school);
      setLoading(false);
    } else if (!schoolLoading) {
      setLoading(false);
    }
  }, [school, schoolLoading]);

  if (authLoading || schoolLoading || (!isStudent && !isAppUser && studentsQuery.isLoading))
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh", background: "#f5f7fb" }}>
        <CircularProgress size={56} sx={{ color: "#0168EA" }} />
      </Box>
    );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const selectedClassLabel = selectedClass
    ? `${selectedClass.name}${selectedClass.section ? ` - ${selectedClass.section}` : ""}`
    : "-";

  const mediumDisplay =
    selectedMedium === "ALL"
      ? Array.isArray(data?.medium)
        ? data.medium.join(", ")
        : data?.medium || "-"
      : selectedMedium || "-";

  // Stats cards — role based  (displayType drives card value rendering)
  type StatCard = { label: string; value: number | string; icon: any; accentColor: string; iconBg: string; displayType: "number" | "session" | "text" | "medium" };
  const statsCards: StatCard[] = isStudent
    ? [
        { label: "My Class",            value: selectedClassLabel,          icon: ClassIcon,       accentColor: "#2563EB", iconBg: "#DBEAFE", displayType: "text" },
        { label: t("session") || "Session", value: displaySession?.name || "-", icon: CalendarTodayIcon, accentColor: "#0891B2", iconBg: "#CFFAFE", displayType: "session" },
        { label: t("medium") || "Medium",  value: mediumDisplay,             icon: PublicIcon,      accentColor: "#7C3AED", iconBg: "#EDE9FE", displayType: "medium" },
      ]
    : isTeacher
    ? [
        { label: t("total_students") || "Total Students", value: totalStudentsCount,          icon: PeopleAltIcon,     accentColor: "#2563EB", iconBg: "#DBEAFE", displayType: "number" },
        { label: t("classes") || "Classes",               value: classesQuery.data?.length ?? "-", icon: ClassIcon,    accentColor: "#059669", iconBg: "#D1FAE5", displayType: "number" },
        { label: t("active_session") || "Active Session", value: displaySession?.name || "-",  icon: CalendarTodayIcon, accentColor: "#0891B2", iconBg: "#CFFAFE", displayType: "session" },
        { label: t("medium") || "Medium",                 value: mediumDisplay,               icon: PublicIcon,        accentColor: "#4338CA", iconBg: "#E0E7FF", displayType: "medium" },
      ]
    : isAppUser
    ? [
        { label: t("active_session") || "Active Session", value: displaySession?.name || "-", icon: CalendarTodayIcon, accentColor: "#0891B2", iconBg: "#CFFAFE", displayType: "session" },
        { label: t("medium") || "Medium",                 value: mediumDisplay,              icon: PublicIcon,        accentColor: "#4338CA", iconBg: "#E0E7FF", displayType: "medium" },
      ]
    : [
        { label: t("total_students") || "Total Students", value: totalStudentsCount,              icon: PeopleAltIcon,     accentColor: "#2563EB", iconBg: "#DBEAFE", displayType: "number" },
        { label: t("teachers") || "Teachers",             value: teachersQuery.data?.length ?? "-", icon: PersonAddIcon,    accentColor: "#7C3AED", iconBg: "#EDE9FE", displayType: "number" },
        { label: t("classes") || "Classes",               value: classesQuery.data?.length ?? "-",  icon: ClassIcon,        accentColor: "#059669", iconBg: "#D1FAE5", displayType: "number" },
        { label: "Pending Applications", value: pendingApplicationsCount, icon: HowToRegIcon, accentColor: "#DC2626", iconBg: "#FEE2E2", displayType: "number" },
        { label: t("active_session") || "Active Session", value: displaySession?.name || "-",       icon: CalendarTodayIcon, accentColor: "#0891B2", iconBg: "#CFFAFE", displayType: "session" },
        { label: t("medium") || "Medium",                 value: mediumDisplay,                     icon: PublicIcon,        accentColor: "#4338CA", iconBg: "#E0E7FF", displayType: "medium" },
      ];

  // Quick actions — role based
  const quickActions = isStudent
    ? [
        { label: t("admit_card") || "Admit Card", icon: AssignmentIcon, path: "/student/admit-cards", color: "#0168EA", bg: "#EEF4FF" },
        { label: "Marksheet", icon: MenuBookIcon, path: "/student/marksheet-marks", color: "#2E7D32", bg: "#F1F8F1" },
        { label: t("tc_list") || "TC", icon: SwapHorizIcon, path: "/student/tc-list", color: "#E65100", bg: "#FFF3E0" },
        { label: t("my_profile") || "My Profile", icon: PersonIcon, path: "/student/profile", color: "#7B1FA2", bg: "#F5EEF8" },
      ]
    : isTeacher
    ? [
        { label: t("students") || "Students", icon: PeopleAltIcon, path: "/teacher/students", color: "#0168EA", bg: "#EEF4FF" },
        { label: t("attendance") || "Attendance", icon: HowToRegIcon, path: "/teacher/attendance-management", color: "#2E7D32", bg: "#F1F8F1" },
        { label: t("fee_receipt") || "Fee Receipt", icon: ReceiptIcon, path: "/teacher/fee-receipts", color: "#E65100", bg: "#FFF3E0" },
        { label: t("id_card") || "ID Card", icon: BadgeIcon, path: "/teacher/id-card", color: "#00838F", bg: "#E0F7FA" },
        { label: t("admit_card") || "Admit Card", icon: AssignmentIcon, path: "/teacher/admit-cards", color: "#C62828", bg: "#FFEBEE" },
        { label: "Marksheet", icon: MenuBookIcon, path: "/teacher/create-marksheet", color: "#1565C0", bg: "#E3F2FD" },
        { label: t("tc_list") || "TC", icon: SwapHorizIcon, path: "/teacher/tc-management", color: "#4A148C", bg: "#EDE7F6" },
        { label: t("subjects") || "Subjects", icon: SubjectIcon, path: "/teacher/subjects", color: "#827717", bg: "#F9FBE7" },
      ]
    : [
        { label: t("students") || "Students", icon: PeopleAltIcon, path: "/school-admin/students", color: "#0168EA", bg: "#EEF4FF" },
        { label: t("teachers") || "Teachers", icon: PersonAddIcon, path: "/school-admin/teachers", color: "#7B1FA2", bg: "#F5EEF8" },
        { label: t("attendance") || "Attendance", icon: HowToRegIcon, path: "/school-admin/attendance-management", color: "#2E7D32", bg: "#F1F8F1" },
        { label: t("fee_receipt") || "Fee Receipt", icon: ReceiptIcon, path: "/school-admin/fee-receipts", color: "#E65100", bg: "#FFF3E0" },
        { label: t("id_card") || "ID Card", icon: BadgeIcon, path: "/school-admin/id-card", color: "#00838F", bg: "#E0F7FA" },
        { label: t("admit_card") || "Admit Card", icon: AssignmentIcon, path: "/school-admin/admit-cards", color: "#C62828", bg: "#FFEBEE" },
        { label: "Marksheet", icon: MenuBookIcon, path: "/school-admin/create-marksheet", color: "#1565C0", bg: "#E3F2FD" },
        { label: t("tc_list") || "TC", icon: SwapHorizIcon, path: "/school-admin/tc-management", color: "#4A148C", bg: "#EDE7F6" },
      ];

  const schoolDetailItems = [
    { icon: CodeIcon, label: t("school_code") || "School Code", value: data?.schoolCode, color: "#0168EA" },
    { icon: PublicIcon, label: t("district") || "District", value: data?.district, color: "#7B1FA2" },
    { icon: PersonIcon, label: t("principal") || "Principal", value: data?.schoolPrincipalName, color: "#2E7D32" },
    { icon: PhoneIcon, label: t("contact") || "Contact", value: data?.principalMobile, color: "#E65100" },
  ];

  return (
    <Box sx={{ background: "#f5f7fb", minHeight: "100vh", pb: 4 }}>

      {/* ─── HERO BANNER ────────────────────────────────────────── */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: 200, md: 300 },
          borderRadius: { xs: 0, md: "0 0 24px 24px" },
          overflow: "hidden",
          mb: 3,
          animation: `${fadeIn} 0.7s ease both`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${schoolImageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            animation: `${heroScale} 0.9s ease both`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to bottom, rgba(0,0,0,0.04) 0%, rgba(0,10,40,0.62) 100%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            p: { xs: 2, md: 3 },
            color: "white",
          }}
        >
          <Typography
            fontWeight="bold"
            sx={{
              fontSize: { xs: "1.2rem", md: "1.9rem" },
              textShadow: "0 2px 8px rgba(0,0,0,0.5)",
              lineHeight: 1.2,
              mb: 0.5,
            }}
          >
            {data?.nameofSchool || t("school_name")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            {(data?.district || data?.state) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LocationOnIcon sx={{ fontSize: "0.95rem", color: "#FFD700" }} />
                <Typography sx={{ fontSize: { xs: "0.72rem", md: "0.85rem" }, opacity: 0.9 }}>
                  {[data.district, data.state].filter(Boolean).join(", ")}
                </Typography>
              </Box>
            )}
            {data?.schoolCode && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CodeIcon sx={{ fontSize: "0.95rem", color: "#90CAF9" }} />
                <Typography sx={{ fontSize: { xs: "0.72rem", md: "0.85rem" }, opacity: 0.9 }}>
                  Code: {data.schoolCode}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {canManageImages && (
          <Box sx={{ position: "absolute", top: 12, right: 12, display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              component="label"
              size="small"
              startIcon={schoolImageFile ? <CheckCircleIcon sx={{ fontSize: "0.9rem" }} /> : <CameraAltIcon sx={{ fontSize: "0.9rem" }} />}
              sx={{
                bgcolor: "rgba(255,255,255,0.88)",
                color: "#333",
                fontSize: "0.7rem",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { bgcolor: "#fff" },
              }}
            >
              {schoolImageFile ? t("photo_selected") || "Selected" : t("change_photo") || "Change Photo"}
              <input type="file" hidden accept="image/*" onChange={handleSchoolImageChange} />
            </Button>
            {hasImageChanges && (
              <Button
                variant="contained"
                size="small"
                onClick={handleUpdateImages}
                disabled={updateSchoolMutation.isLoading}
                sx={{ bgcolor: "#0168EA", fontSize: "0.7rem", textTransform: "none", "&:hover": { bgcolor: "#1565C0" } }}
              >
                {updateSchoolMutation.isLoading ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : t("update") || "Update"}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* ─── STATS ROW ──────────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: `repeat(${Math.min(statsCards.length, 3)}, 1fr)`,
            md: `repeat(${statsCards.length}, 1fr)`,
          },
          gap: { xs: 1.5, md: 2 },
          px: { xs: 1.5, md: 0 },
          mb: 3,
        }}
      >
        {statsCards.map((stat, idx) => (
          <Card
            key={idx}
            sx={{
              background: "#ffffff",
              borderRadius: 2.5,
              boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
              borderTop: `3px solid ${stat.accentColor}`,
              transition: "transform 0.22s, box-shadow 0.22s",
              animation: `${scaleIn} 0.45s ease ${0.25 + idx * 0.07}s both`,
              "&:hover": {
                transform: "translateY(-4px) scale(1.015)",
                boxShadow: `0 10px 28px ${stat.accentColor}28`,
              },
              "&:hover .stat-icon-box": {
                transform: "scale(1.15) rotate(-6deg)",
              },
            }}
          >
            <CardContent sx={{ p: { xs: "14px !important", md: "16px !important" } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", minHeight: 60 }}>
                <Box sx={{ minWidth: 0, flex: 1, pr: 0.5 }}>

                  {/* Label — uniform for all cards */}
                  <Typography sx={{ fontSize: "0.65rem", color: "#8a94a6", fontWeight: 500, mb: 0.6, letterSpacing: 0.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {stat.label}
                  </Typography>

                  {/* NUMBER — animated count */}
                  {stat.displayType === "number" && (
                    <Typography sx={{ fontSize: { xs: "1.25rem", md: "1.4rem" }, fontWeight: 700, lineHeight: 1.1, color: stat.accentColor }}>
                      <AnimatedNumber value={stat.value} />
                    </Typography>
                  )}

                  {/* SESSION / TEXT — same size as number, no wrap */}
                  {(stat.displayType === "session" || stat.displayType === "text") && (
                    <Typography sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" }, fontWeight: 700, lineHeight: 1.1, color: stat.accentColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {String(stat.value)}
                    </Typography>
                  )}

                  {/* MEDIUM — 2 chips max + Tooltip on "+N more" */}
                  {stat.displayType === "medium" && (() => {
                    const list = String(stat.value) === "-" ? [] : String(stat.value).split(", ").filter(Boolean);
                    const shown = list.slice(0, 2);
                    const extra = list.slice(2);
                    const chipSx = { px: "7px", py: "3px", borderRadius: "5px", bgcolor: `${stat.accentColor}18`, color: stat.accentColor, fontSize: "0.6rem", fontWeight: 700, lineHeight: 1.5, whiteSpace: "nowrap" as const };
                    return list.length === 0
                      ? <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: stat.accentColor }}>-</Typography>
                      : (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4, mt: 0.3 }}>
                          {shown.map((m, i) => <Box key={i} sx={chipSx}>{m}</Box>)}
                          {extra.length > 0 && (
                            <Tooltip title={extra.join(" · ")} placement="top" arrow>
                              <Box sx={{ ...chipSx, bgcolor: "#f1f5f9", color: "#64748b", cursor: "pointer" }}>
                                +{extra.length} more
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      );
                  })()}
                </Box>

                {/* Icon — uniform for all cards */}
                <Box
                  className="stat-icon-box"
                  sx={{
                    bgcolor: stat.iconBg,
                    borderRadius: 2,
                    p: { xs: 0.85, md: 1.0 },
                    display: "flex",
                    flexShrink: 0,
                    ml: 1,
                    mt: 0.25,
                    transition: "transform 0.25s ease",
                  }}
                >
                  <stat.icon sx={{ fontSize: { xs: "1.15rem", md: "1.3rem" }, color: stat.accentColor }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* ─── MAIN CONTENT GRID ──────────────────────────────────── */}
      <Box sx={{ px: { xs: 1.5, md: 0 } }}>
        <Grid container spacing={2.5}>

          {/* QUICK ACTIONS */}
          <Grid item xs={12} md={isStudent ? 12 : 7}>
            <Card sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", height: "100%", animation: `${fadeSlideUp} 0.5s ease 0.2s both` }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
                  <Box sx={{ width: 4, height: 18, borderRadius: 2, bgcolor: "#2563EB" }} />
                  <Typography fontWeight="bold" sx={{ color: "#1e293b", fontSize: "0.93rem" }}>
                    Quick Actions
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(4, 1fr)" },
                    gap: { xs: 1, md: 1.5 },
                  }}
                >
                  {quickActions.map((action, idx) => (
                    <Box
                      key={idx}
                      onClick={() => navigate(action.path)}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: { xs: 1.25, md: 1.75 },
                        borderRadius: 2.5,
                        cursor: "pointer",
                        bgcolor: "#fff",
                        border: `1.5px solid ${action.color}22`,
                        transition: "all 0.2s ease",
                        animation: `${fadeSlideUp} 0.38s ease ${0.35 + idx * 0.045}s both`,
                        "&:hover": {
                          transform: "translateY(-4px) scale(1.04)",
                          bgcolor: action.bg,
                          boxShadow: `0 6px 20px ${action.color}28`,
                          border: `1.5px solid ${action.color}55`,
                        },
                        "&:hover .action-icon-circle": {
                          transform: "scale(1.18) rotate(-8deg)",
                        },
                      }}
                    >
                      <Box
                        className="action-icon-circle"
                        sx={{
                          width: { xs: 40, md: 48 },
                          height: { xs: 40, md: 48 },
                          borderRadius: "50%",
                          bgcolor: action.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 0.75,
                          transition: "transform 0.25s ease",
                        }}
                      >
                        <action.icon sx={{ color: action.color, fontSize: { xs: "1.15rem", md: "1.35rem" } }} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: { xs: "0.62rem", md: "0.7rem" },
                          fontWeight: 600,
                          color: action.color,
                          textAlign: "center",
                          lineHeight: 1.25,
                        }}
                      >
                        {action.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* IMPORTANT LINKS — non-student desktop */}
          {!isStudent && (
            <Grid item xs={12} md={4} sx={{ order: { md: 3 } }}>
              <Card sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", height: "100%", animation: `${fadeSlideUp} 0.5s ease 0.32s both` }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Box sx={{ width: 4, height: 18, borderRadius: 2, bgcolor: "#7C3AED" }} />
                    <Typography fontWeight="bold" sx={{ color: "#1e293b", fontSize: "0.93rem" }}>
                      {t("important_links") || "Important Links"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    {importantLinks.map((section, idx) => (
                      <Box key={idx} sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid rgba(0,0,0,0.07)" }}>
                        <Box
                          onClick={() => handleMenuClick(idx)}
                          sx={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            cursor: "pointer", px: 1.5, py: 1,
                            background: openMenu === idx ? "#EEF4FF" : "#f8faff",
                            color: openMenu === idx ? "#2563EB" : "#334155",
                            fontWeight: 700, fontSize: 13,
                            transition: "all 0.2s",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <span style={{ fontSize: "1rem" }}>{section.icon}</span>
                            {section.label}
                          </Box>
                          {openMenu === idx ? (
                            <ExpandLessIcon sx={{ fontSize: "1.1rem" }} />
                          ) : (
                            <ExpandMoreIcon sx={{ color: "#94a3b8", fontSize: "1.1rem" }} />
                          )}
                        </Box>
                        <Collapse in={openMenu === idx} timeout={220} unmountOnExit>
                          <Box sx={{ px: 1.25, py: 1, display: "flex", flexDirection: "column", gap: 0.6, maxHeight: 180, overflowY: "auto", bgcolor: "#fff" }}>
                            {section.links.map((link: any, lidx: number) => (
                              <Button
                                key={lidx}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  justifyContent: "flex-start", textAlign: "left",
                                  color: "#1565C0", bgcolor: "#F5F9FF",
                                  border: "1px solid #DCEEFB", borderRadius: 1.5,
                                  fontWeight: 500, fontSize: 11.5, px: 1.25, py: 0.5,
                                  textTransform: "none", whiteSpace: "normal",
                                  wordBreak: "break-all", width: "100%",
                                  "&:hover": { bgcolor: "#0168EA", color: "#fff", border: "1px solid #0168EA" },
                                }}
                              >
                                {link.text}
                              </Button>
                            ))}
                          </Box>
                        </Collapse>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* IMPORTANT LINKS — student full width */}
          {isStudent && (
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", animation: `${fadeSlideUp} 0.5s ease 0.32s both` }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Box sx={{ width: 4, height: 18, borderRadius: 2, bgcolor: "#7C3AED" }} />
                    <Typography fontWeight="bold" sx={{ color: "#1e293b", fontSize: "0.93rem" }}>
                      {t("important_links") || "Important Links"}
                    </Typography>
                  </Box>
                  <Grid container spacing={1.5}>
                    {importantLinks.map((section, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <Box sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid rgba(0,0,0,0.07)" }}>
                          <Box
                            onClick={() => handleMenuClick(idx)}
                            sx={{
                              display: "flex", alignItems: "center", justifyContent: "space-between",
                              cursor: "pointer", px: 1.5, py: 1,
                              background: openMenu === idx ? section.color : "rgba(240,244,255,0.8)",
                              color: openMenu === idx ? "#fff" : "#333",
                              fontWeight: 700, fontSize: 13, transition: "all 0.2s",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <span>{section.icon}</span>{section.label}
                            </Box>
                            {openMenu === idx ? <ExpandLessIcon sx={{ fontSize: "1rem", color: "#2563EB" }} /> : <ExpandMoreIcon sx={{ color: "#94a3b8", fontSize: "1rem" }} />}
                          </Box>
                          <Collapse in={openMenu === idx} timeout={220} unmountOnExit>
                            <Box sx={{ px: 1.25, py: 1, display: "flex", flexDirection: "column", gap: 0.6, maxHeight: 180, overflowY: "auto", bgcolor: "#fff" }}>
                              {section.links.map((link: any, lidx: number) => (
                                <Button
                                  key={lidx}
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    justifyContent: "flex-start", textAlign: "left",
                                    color: "#1565C0", bgcolor: "#F5F9FF",
                                    border: "1px solid #DCEEFB", borderRadius: 1.5,
                                    fontWeight: 500, fontSize: 11, px: 1.25, py: 0.5,
                                    textTransform: "none", whiteSpace: "normal",
                                    wordBreak: "break-all", width: "100%",
                                    "&:hover": { bgcolor: "#0168EA", color: "#fff" },
                                  }}
                                >
                                  {link.text}
                                </Button>
                              ))}
                            </Box>
                          </Collapse>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* SCHOOL DETAILS */}
          {!isStudent && !isAppUser && (
            <Grid item xs={12} md={5} sx={{ order: { md: 2 } }}>
              <Card sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", height: "100%", animation: `${fadeSlideUp} 0.5s ease 0.42s both` }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Box sx={{ width: 4, height: 18, borderRadius: 2, bgcolor: "#059669" }} />
                    <Typography fontWeight="bold" sx={{ color: "#1e293b", fontSize: "0.93rem" }}>
                      {t("school_details") || "School Details"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {/* Row 1: School Code + District */}
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid #f1f5f9" }}>
                      {schoolDetailItems.slice(0, 2).map((item, idx) => (
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, p: "10px 12px", borderRight: idx === 0 ? "1px solid #f1f5f9" : "none", transition: "background 0.15s", "&:hover": { bgcolor: "#f8faff" } }}>
                          <item.icon sx={{ color: item.color, fontSize: "1.05rem", flexShrink: 0 }} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: "0.6rem", color: "#a0aec0", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.3 }}>{item.label}</Typography>
                            <Typography sx={{ fontSize: "0.82rem", color: "#1e293b", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.value || "-"}</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    {/* Row 2: Principal + Contact */}
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid #f1f5f9" }}>
                      {schoolDetailItems.slice(2, 4).map((item, idx) => (
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, p: "10px 12px", borderRight: idx === 0 ? "1px solid #f1f5f9" : "none", transition: "background 0.15s", "&:hover": { bgcolor: "#f8faff" } }}>
                          <item.icon sx={{ color: item.color, fontSize: "1.05rem", flexShrink: 0 }} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: "0.6rem", color: "#a0aec0", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.3 }}>{item.label}</Typography>
                            <Typography sx={{ fontSize: "0.82rem", color: "#1e293b", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.value || "-"}</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    {/* Row 3: Medium — full width */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "10px 12px", transition: "background 0.15s", "&:hover": { bgcolor: "#f8faff" } }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                        <LanguageIcon sx={{ color: "#4338CA", fontSize: "1.05rem", flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.6rem", color: "#a0aec0", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.3 }}>{t("medium") || "Medium"}</Typography>
                          <Typography sx={{ fontSize: "0.82rem", color: "#1e293b", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{mediumDisplay}</Typography>
                        </Box>
                      </Box>
                      {(isSchoolAdmin || isTeacher) && (
                        <IconButton size="small" onClick={() => setMediumDialogOpen(true)} sx={{ color: "#4338CA", flexShrink: 0, bgcolor: "#EEF2FF", "&:hover": { bgcolor: "#c7d2fe" } }}>
                          <EditIcon sx={{ fontSize: "0.9rem" }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                </CardContent>
              </Card>
            </Grid>
          )}

          {/* UPCOMING HOLIDAYS */}
          {!isStudent && (
            <Grid item xs={12} md={!isAppUser ? 4 : 6} sx={{ order: { md: 4 } }}>
              <Card sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", height: "100%", animation: `${fadeSlideUp} 0.5s ease 0.5s both` }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Box sx={{ width: 4, height: 18, borderRadius: 2, bgcolor: "#0891B2" }} />
                    <Typography fontWeight="bold" sx={{ color: "#1e293b", fontSize: "0.93rem" }}>
                      Upcoming Holidays
                    </Typography>
                  </Box>
                  {useholidaysQueryBySession.isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                      <CircularProgress size={22} sx={{ color: "#0168EA" }} />
                    </Box>
                  ) : upcomingHolidays.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <BeachAccessIcon sx={{ fontSize: 40, color: "#ddd", mb: 1 }} />
                      <Typography sx={{ fontSize: "0.82rem", color: "#bbb" }}>
                        {"No upcoming holidays"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {upcomingHolidays.map((holiday, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: "flex", alignItems: "center", gap: 1.5,
                            p: 1.25, borderRadius: 2,
                            bgcolor: "#f8faff", border: "1px solid #e8effe",
                            animation: `${fadeSlideUp} 0.35s ease ${idx * 0.09}s both`,
                            transition: "background 0.15s",
                            "&:hover": { bgcolor: "#EEF4FF" },
                          }}
                        >
                          <Box
                            sx={{
                              textAlign: "center", minWidth: 44,
                              bgcolor: "#DBEAFE", borderRadius: 1.5,
                              py: 0.5, px: 1, flexShrink: 0,
                            }}
                          >
                            <Typography sx={{ fontSize: "0.6rem", color: "#2563EB", lineHeight: 1, textTransform: "uppercase", fontWeight: 600 }}>
                              {holiday.date.toLocaleDateString("en", { month: "short" })}
                            </Typography>
                            <Typography sx={{ fontSize: "1rem", color: "#1e40af", fontWeight: "bold", lineHeight: 1.2 }}>
                              {holiday.date.getDate()}
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {holiday.description || t("holiday") || "Holiday"}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.3, flexWrap: "wrap" }}>
                              <Box sx={{
                                px: "7px", py: "2px", borderRadius: "5px", fontSize: "0.58rem", fontWeight: 700, lineHeight: 1.5,
                                ...(holiday.typeName === "FESTIVAL"    ? { bgcolor: "#EDE9FE", color: "#7C3AED" } :
                                    holiday.typeName === "VACATION"    ? { bgcolor: "#D1FAE5", color: "#059669" } :
                                    holiday.typeName === "EXAM_BREAK"  ? { bgcolor: "#FEE2E2", color: "#DC2626" } :
                                    holiday.typeName === "NATIONAL_HOLIDAY" ? { bgcolor: "#DBEAFE", color: "#2563EB" } :
                                                                         { bgcolor: "#F1F5F9", color: "#64748B" }),
                              }}>
                                {holiday.typeName.replace(/_/g, " ")}
                              </Box>
                              {holiday.typeDescription && (
                                <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {holiday.typeDescription}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* FEE SUMMARY — school admin only */}
          {isSchoolAdmin && (
            <Grid item xs={12} md={4} sx={{ order: { md: 5 } }}>
              <Card
                sx={{
                  borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", height: "100%",
                  // transition: "box-shadow 0.18s, transform 0.2s",
                  // animation: `${fadeSlideUp} 0.5s ease 0.57s both`,
                  // "&:hover": { boxShadow: "0 6px 24px rgba(5,150,105,0.14)", transform: "translateY(-2px)" },
                }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Box sx={{ width: 4, height: 18, borderRadius: 2, bgcolor: "#059669" }} />
                    <Typography fontWeight="bold" sx={{ color: "#1e293b", fontSize: "0.93rem" }}>
                      Fee Summary
                    </Typography>
                  </Box>
                  {/* Session info row */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, p: 1.25, borderRadius: 2, bgcolor: "#f8faff", border: "1px solid #e8effe", mb: 1 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "#EEF4FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <ReceiptIcon sx={{ color: "#2563EB", fontSize: "1.1rem" }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", fontWeight: 500 }}>{"Current Session"}</Typography>
                      <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#1e293b" }}>
                        {displaySession?.name || "-"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action rows — uniform simple style */}
                  {[
                    { label: "View Fee Details", icon: ReceiptIcon,      path: "/school-admin/fee-receipts" },
                    { label: "Manage Fee Structures", icon: TrendingUpIcon, path: "/school-admin/fee-structure" },
                    { label: t("fee_transactions") || "Fee Transactions", icon: AccountBalanceIcon, path: "/school-admin/fee-transactions" },
                  ].map((item, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex", alignItems: "center", gap: 1.25,
                        p: 1.25, borderRadius: 2,
                        bgcolor: "#fff", border: "1px solid #e8effe",
                        cursor: "pointer", mt: 1,
                        transition: "background 0.15s, border-color 0.15s",
                        "&:hover": { bgcolor: "#f8faff", borderColor: "#c7d7fd" },
                      }}
                      onClick={(e) => { e.stopPropagation(); navigate(item.path); }}
                    >
                      <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <item.icon sx={{ color: "#64748b", fontSize: "1rem" }} />
                      </Box>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: "#334155", flex: 1 }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>→</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

        </Grid>
      </Box>



      {/* ─── MEDIUM MANAGEMENT DIALOG ───────────────────────────── */}
      <Dialog open={mediumDialogOpen} onClose={() => setMediumDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LanguageIcon sx={{ color: "#0168EA" }} />
            <Typography variant="h6" fontWeight="bold">{t("medium_management")}</Typography>
          </Box>
          <IconButton onClick={() => setMediumDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1.5, color: "#666", fontWeight: 600 }}>{t("current_mediums")}:</Typography>
            {mediums.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {mediums.map((medium) => (
                  <Chip key={medium} label={medium} onDelete={() => handleRemoveMedium(medium)} deleteIcon={<DeleteIcon />} color="primary" variant="outlined" sx={{ fontSize: "0.9rem", fontWeight: 600 }} />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mb: 2, color: "#999", fontStyle: "italic" }}>{t("no_mediums_added")}</Typography>
            )}
            <Autocomplete
              freeSolo
              options={["HINDI","ENGLISH","MARATHI","KANNADA","TELUGU","TAMIL","GUJARATI","ODIA","PUNJABI","BENGALI","URDU"].filter((o) => !mediums.includes(o))}
              value={newMedium}
              onChange={(_, newValue) => setNewMedium(newValue || "")}
              onInputChange={(_, newInputValue) => setNewMedium(newInputValue.toUpperCase())}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("add_medium")}
                  placeholder={t("type_or_select_medium")}
                  variant="outlined"
                  size="small"
                  sx={{ backgroundColor: "#f8f9fb" }}
                  onKeyPress={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddMedium(); } }}
                />
              )}
              sx={{ mb: 2 }}
            />
            <Button variant="outlined" onClick={handleAddMedium} disabled={!newMedium.trim() || mediums.includes(newMedium.trim().toUpperCase())} startIcon={<LanguageIcon />} sx={{ color: "#0168EA", borderColor: "#0168EA" }}>
              {t("add_medium")}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setMediumDialogOpen(false)} variant="outlined">{t("cancel")}</Button>
          <Button
            onClick={() => { handleUpdateMediums(); setMediumDialogOpen(false); }}
            variant="contained"
            disabled={!hasMediumChanges || updateSchoolMutation.isLoading}
            sx={{ bgcolor: "#0168EA", "&:hover": { bgcolor: "#1565C0" } }}
          >
            {updateSchoolMutation.isLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : t("update_mediums")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
